import React, { useState } from 'react';
import { Briefcase, MapPin, Clock, Users, Search, Filter, Bookmark, ExternalLink, Eye, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { mockJobs } from '../data/mock';
import { toast } from '../hooks/use-toast';

const Jobs = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [savedJobs, setSavedJobs] = useState([]);

  const allJobs = [
    ...mockJobs,
    {
      id: "3",
      title: "E-Learning Development Specialist",
      company: "Google",
      location: "Remote",
      type: "Full-time",
      posted: "3 days ago",
      applicants: 156,
      logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Google_2015_logo.svg/50px-Google_2015_logo.svg.png",
      description: "Join our team to create innovative e-learning experiences for our global workforce...",
      salary: "$95,000 - $125,000",
      skills: ["Instructional Design", "E-Learning", "LMS", "SCORM"]
    },
    {
      id: "4",
      title: "Corporate Training Manager",
      company: "Meta",
      location: "Menlo Park, CA",
      type: "Full-time",
      posted: "5 days ago",
      applicants: 89,
      logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Meta_Platforms_Inc._logo.svg/50px-Meta_Platforms_Inc._logo.svg.png",
      description: "Lead training initiatives across multiple departments and drive learning culture...",
      salary: "$110,000 - $140,000",
      skills: ["Leadership", "Training", "Program Management", "Analytics"]
    },
    {
      id: "5",
      title: "Virtual Training Coordinator",
      company: "Zoom",
      location: "San Jose, CA",
      type: "Contract",
      posted: "1 week ago",
      applicants: 67,
      logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Zoom_Communications_Logo.svg/50px-Zoom_Communications_Logo.svg.png",
      description: "Coordinate and facilitate virtual training sessions for our enterprise clients...",
      salary: "$70 - $85 /hour",
      skills: ["Virtual Training", "Zoom", "Training Coordination", "Client Management"]
    }
  ];

  const handleSaveJob = (jobId) => {
    if (savedJobs.includes(jobId)) {
      setSavedJobs(savedJobs.filter(id => id !== jobId));
      toast({
        title: "Job unsaved",
        description: "Job removed from your saved jobs.",
      });
    } else {
      setSavedJobs([...savedJobs, jobId]);
      toast({
        title: "Job saved!",
        description: "Job added to your saved jobs.",
      });
    }
  };

  const handleApply = (jobTitle, jobId) => {
    // Here you would typically redirect to application form or open modal
    toast({
      title: "Application started!",
      description: `Redirecting to application for ${jobTitle}...`,
    });
    
    // Simulate redirect to application page
    setTimeout(() => {
      // window.open(`/apply/${jobId}`, '_blank');
      toast({
        title: "Application Page",
        description: "This would open the job application form.",
      });
    }, 1000);
  };

  const [selectedJobDetails, setSelectedJobDetails] = useState(null);

  const handleViewDetails = (job) => {
    setSelectedJobDetails(job);
  };

  const closeJobDetails = () => {
    setSelectedJobDetails(null);
  };

  const filteredJobs = allJobs.filter(job =>
    job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <Briefcase className="w-6 h-6 mr-3 text-blue-600" />
            Training Jobs
          </CardTitle>
          <p className="text-sm text-gray-600">
            Discover amazing opportunities in the training and learning development field
          </p>
        </CardHeader>
      </Card>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search jobs by title, company, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" className="flex items-center">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">All Jobs ({filteredJobs.length})</TabsTrigger>
          <TabsTrigger value="saved">Saved ({savedJobs.length})</TabsTrigger>
          <TabsTrigger value="applied">Applied (2)</TabsTrigger>
          <TabsTrigger value="recommended">Recommended (5)</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {filteredJobs.map((job) => (
            <Card key={job.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={job.logo} alt={job.company} />
                      <AvatarFallback>{job.company.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg hover:text-blue-600 cursor-pointer mb-1">
                        {job.title}
                      </h3>
                      <p className="text-blue-600 font-medium mb-2 hover:underline cursor-pointer">
                        {job.company}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {job.location}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {job.posted}
                        </div>
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {job.applicants} applicants
                        </div>
                      </div>
                      
                      {job.salary && (
                        <p className="text-sm font-medium text-green-600 mb-2">{job.salary}</p>
                      )}
                      
                      <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                        {job.description}
                      </p>
                      
                      {job.skills && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {job.skills.map((skill, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2">
                        <Badge variant={job.type === 'Full-time' ? 'default' : 'outline'}>
                          {job.type}
                        </Badge>
                        {job.type === 'Remote' && (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            Remote
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(job)}
                      className="flex items-center"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </Button>
                    <Button
                      variant={savedJobs.includes(job.id) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleSaveJob(job.id)}
                      className="flex items-center"
                    >
                      <Bookmark className={`w-4 h-4 mr-1 ${savedJobs.includes(job.id) ? 'fill-current' : ''}`} />
                      {savedJobs.includes(job.id) ? 'Saved' : 'Save for Later'}
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => handleApply(job.title, job.id)}
                      className="flex items-center"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Apply Now
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="saved" className="space-y-4">
          {savedJobs.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Bookmark className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No saved jobs</h3>
                <p className="text-gray-600 mb-4">Save jobs you're interested in to view them here later.</p>
                <Button>Browse Jobs</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredJobs.filter(job => savedJobs.includes(job.id)).map((job) => (
                <Card key={job.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={job.logo} alt={job.company} />
                          <AvatarFallback>{job.company.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg hover:text-blue-600 cursor-pointer mb-1">
                            {job.title}
                          </h3>
                          <p className="text-blue-600 font-medium mb-2">{job.company}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                            <span>{job.location}</span>
                            <span>•</span>
                            <span>{job.posted}</span>
                          </div>
                          <p className="text-sm text-gray-700 mb-3">{job.description}</p>
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2 ml-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSaveJob(job.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Remove
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => handleApply(job.title)}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="applied" className="space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <Briefcase className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Applied Jobs</h3>
              <p className="text-gray-600 mb-4">
                Track the status of your job applications here.
              </p>
              <Badge variant="secondary" className="mb-4">Feature Coming Soon</Badge>
              <p className="text-sm text-gray-500">
                This feature will be available in the next update.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recommended" className="space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Recommended Jobs</h3>
              <p className="text-gray-600 mb-4">
                Get personalized job recommendations based on your profile and activity.
              </p>
              <Badge variant="secondary" className="mb-4">Feature Coming Soon</Badge>
              <p className="text-sm text-gray-500">
                Our AI will analyze your skills and preferences to suggest the best opportunities.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Job Details Modal */}
      {selectedJobDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">{selectedJobDetails.title}</h2>
              <Button variant="ghost" size="sm" onClick={closeJobDetails}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Company Info */}
              <div className="flex items-center space-x-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={selectedJobDetails.logo} />
                  <AvatarFallback>{selectedJobDetails.company[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-xl">{selectedJobDetails.company}</h3>
                  <div className="flex items-center space-x-4 text-gray-600 mt-1">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {selectedJobDetails.location}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {selectedJobDetails.posted}
                    </div>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {selectedJobDetails.applicants} applicants
                    </div>
                  </div>
                </div>
              </div>

              {/* Job Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Job Type</h4>
                  <Badge variant="outline">{selectedJobDetails.type}</Badge>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Salary Range</h4>
                  <p className="text-gray-700">{selectedJobDetails.salary}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Experience Level</h4>
                  <p className="text-gray-700">Mid to Senior Level</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h4 className="font-semibold mb-3">Job Description</h4>
                <p className="text-gray-700 leading-relaxed">
                  {selectedJobDetails.description}
                </p>
                <div className="mt-4">
                  <p className="text-gray-700">We are looking for an experienced professional to join our dynamic team. This role offers excellent growth opportunities and the chance to work with cutting-edge technologies in a collaborative environment.</p>
                </div>
              </div>

              {/* Required Skills */}
              {selectedJobDetails.skills && (
                <div>
                  <h4 className="font-semibold mb-3">Required Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedJobDetails.skills.map((skill, index) => (
                      <Badge key={index} variant="secondary">{skill}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-4 pt-4 border-t">
                <Button 
                  onClick={() => handleApply(selectedJobDetails.title, selectedJobDetails.id)}
                  className="flex-1"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Apply Now
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => handleSaveJob(selectedJobDetails.id)}
                  className="flex items-center"
                >
                  <Bookmark className={`w-4 h-4 mr-2 ${savedJobs.includes(selectedJobDetails.id) ? 'fill-current' : ''}`} />
                  {savedJobs.includes(selectedJobDetails.id) ? 'Saved' : 'Save for Later'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;